import { useCallback, useEffect, useState } from 'react';
import { sentry } from '@frontend/tracking';

type VersionResponse = {
  version: string;
  metadata?: {
    urgency: 'low' | 'high';
  };
};

/**
 * Keep this for manual testing purposes
 * 
const _mockGetVersion = () => {
  return new Promise<VersionResponse>((resolve) => {
    setTimeout(() => {
      resolve({
        version: `${Math.floor(Date.now() / 30)}`,
        metadata: {
          urgency: 'low',
        },
      });
    }, 1000);
  });
};
*/

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
const LAST_UPDATE_TIMESTAMP_KEY = 'weave_last_update_timestamp';
const UPDATE_DELAY_PERIOD = FOUR_HOURS_MS;

/**
 * Asynchronously prefetches and caches the main index.html file and its assets
 *
 * @param url The base URL to prefetch assets from
 */
export const prefetchNewVersion = async (baseUrl?: string): Promise<void> => {
  if (!baseUrl) return;

  try {
    // Extract the base URL without any path segments
    const urlObj = new URL(baseUrl);
    const rootUrl = `${urlObj.protocol}//${urlObj.host}`;

    console.info('Prefetching new version from:', rootUrl);

    // Fetch the index.html and force it to be cached
    const response = await fetch(rootUrl, {
      method: 'GET',
      cache: 'force-cache',
      headers: {
        'Cache-Control': 'max-age=31536000', // Cache for a year
      },
    });

    if (!response.ok) {
      console.error(`Failed to prefetch: ${response.status} ${response.statusText}`);
      sentry.error({
        topic: 'prefetch',
        error: `Failed to prefetch: ${response.status} ${response.statusText}`,
        addContext: {
          name: 'Prefetch Error',
          context: {
            url: rootUrl,
            status: response.status,
            statusText: response.statusText,
          },
        },
      });
      return;
    }

    const html = await response.text();

    // Extract various asset URLs from HTML
    const assetUrls: string[] = [];

    // Extract JavaScript
    const scriptMatches = html.match(/<script[^>]+src="([^"]+)"/g);
    if (scriptMatches) {
      scriptMatches.forEach((match) => {
        const url = match.match(/src="([^"]+)"/)?.[1];
        if (url && (url.startsWith('/') || url.startsWith(rootUrl) || url.startsWith('http'))) {
          assetUrls.push(url);
        }
      });
    }

    // Extract CSS
    const cssMatches = html.match(/<link[^>]+href="([^"]+)"/g);
    if (cssMatches) {
      cssMatches.forEach((match) => {
        const url = match.match(/href="([^"]+)"/)?.[1];
        if (url && (url.startsWith('/') || url.startsWith(rootUrl) || url.startsWith('http'))) {
          assetUrls.push(url);
        }
      });
    }

    // Extract images (img tags)
    const imgMatches = html.match(/<img[^>]+src="([^"]+)"/g);
    if (imgMatches) {
      imgMatches.forEach((match) => {
        const url = match.match(/src="([^"]+)"/)?.[1];
        if (url && (url.startsWith('/') || url.startsWith(rootUrl) || url.startsWith('http'))) {
          assetUrls.push(url);
        }
      });
    }

    // Extract from background-image in inline styles
    const styleMatches = html.match(/<style[^>]*>([\s\S]*?)<\/style>/g);
    if (styleMatches) {
      styleMatches.forEach((styleBlock) => {
        const bgImageMatches = styleBlock.match(/url\(['"]?([^'"()]+)['"]?\)/g);
        if (bgImageMatches) {
          bgImageMatches.forEach((bgUrl) => {
            const url = bgUrl.match(/url\(['"]?([^'"()]+)['"]?\)/)?.[1];
            if (url && (url.startsWith('/') || url.startsWith(rootUrl) || url.startsWith('http'))) {
              assetUrls.push(url);
            }
          });
        }
      });
    }

    // Deduplicate URLs
    const uniqueAssetUrls = [...new Set(assetUrls)];

    console.info(`Found ${uniqueAssetUrls.length} assets to prefetch`);

    // Prefetch all extracted assets
    await Promise.all(
      uniqueAssetUrls.map(async (url) => {
        try {
          // Handle relative URLs
          let assetUrl = url;
          if (url.startsWith('/')) {
            assetUrl = `${rootUrl}${url}`;
          } else if (!url.startsWith('http')) {
            assetUrl = `${rootUrl}/${url}`;
          }

          await fetch(assetUrl, {
            method: 'GET',
            cache: 'force-cache',
            headers: {
              'Cache-Control': 'max-age=31536000', // Cache for a year
            },
          });
          console.info('Prefetched asset:', assetUrl);
        } catch (error) {
          console.warn('Failed to prefetch asset:', url, error);
        }
      })
    );

    console.info('Prefetch completed. New version is now cached.');
  } catch (error) {
    console.error('Error prefetching new version:', error);
  }
};

export const usePollForUpdates = (frequency: number | 'once', url: string | undefined) => {
  const [currentVersion, setCurrentVersion] = useState<string>();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);

  // Initialize the timestamp from localStorage or null if not available
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState(() => {
    try {
      const storedTimestamp = localStorage.getItem(LAST_UPDATE_TIMESTAMP_KEY);
      return storedTimestamp ? parseInt(storedTimestamp) : Date.now();
    } catch (e) {
      console.warn('Could not access localStorage:', e);
      return Date.now();
    }
  });

  // Update localStorage whenever the timestamp changes
  useEffect(() => {
    if (lastUpdateTimestamp !== null) {
      try {
        localStorage.setItem(LAST_UPDATE_TIMESTAMP_KEY, lastUpdateTimestamp.toString());
      } catch (e) {
        console.warn('Could not write to localStorage:', e);
      }
    }
  }, [lastUpdateTimestamp]);

  const checkForUpdates = useCallback(async () => {
    if (!url) {
      return undefined;
    }
    try {
      console.info('checking for updates');
      const data: VersionResponse = await (await fetch(url))?.json();
      // const data = await _mockGetVersion(); //useful for testing
      if (!data || !data.version) {
        return data;
      }

      //on the first iteration of this check, the version will be undefined, so the first check just sets it
      if (!currentVersion) {
        return setCurrentVersion(data.version);
      }
      if (currentVersion === data.version) {
        return undefined;
      }

      console.info('updating current version from ' + currentVersion + ' to ' + data.version);
      setCurrentVersion(data.version);

      const urgency = data.metadata?.urgency || 'low';
      const now = Date.now();

      // Start prefetching new version to cache regardless of urgency
      const baseUrl = typeof url === 'string' ? new URL(url).origin : undefined;
      // Set prefetching state synchronously before prefetch starts
      setIsPrefetching(true);

      // Run prefetch immediately
      prefetchNewVersion(baseUrl)
        .then(() => setIsPrefetching(false))
        .catch((err) => {
          console.warn('Prefetch failed:', err);
          setIsPrefetching(false);
        });

      if (urgency === 'high') {
        // For high urgency updates, set updateAvailable right away
        return setUpdateAvailable(true);
      }
      if (urgency === 'low') {
        // For low urgency updates, only set updateAvailable if 4+ hours have passed
        const hasBeenLongEnoughSinceLastUpdated =
          !!lastUpdateTimestamp && now - lastUpdateTimestamp >= UPDATE_DELAY_PERIOD;
        if (hasBeenLongEnoughSinceLastUpdated) {
          setUpdateAvailable(true);
          setLastUpdateTimestamp(now);
        } else {
          const timeRemaining = UPDATE_DELAY_PERIOD - (now - lastUpdateTimestamp);
          const minutesRemaining = Math.ceil(timeRemaining / (60 * 1000));
          console.info(
            `Low urgency update available but waiting until ${Math.round(
              UPDATE_DELAY_PERIOD / (60 * 60 * 1000)
            )} hours have passed. Approximately ${minutesRemaining} minutes remaining.`
          );
        }
      }

      return data;
    } catch (error) {
      console.error('error', error);
    }
  }, [url, currentVersion, lastUpdateTimestamp]);

  useEffect(() => {
    if (frequency === 'once') {
      // Call checkForUpdates once and handle any errors
      checkForUpdates().catch((err) => console.error('Initial check failed:', err));
      // No need for cleanup since we're not setting up an interval
      return undefined;
    } else {
      // For numeric frequency, run immediately and then set up polling
      checkForUpdates().catch((err) => console.error('Initial check failed:', err));
      const interval = setInterval(checkForUpdates, frequency);
      return () => clearInterval(interval);
    }
  }, [checkForUpdates, frequency]);

  return {
    updateAvailable,
    currentVersion,
    isPrefetching,
    checkForUpdates, // Expose this for manual triggering
  };
};
